import { ReactNode } from "react";
// MUI
import { Stack, Typography } from "@mui/material";

// CUSTOM COMPONENTS
import PositionsMultiSelect from "./PositionsMultiSelect";
import UsersMultiSelect from "./UsersMultiSelect";
import UsersMultiSelectFooter from "./UsersMultiSelectFooter";

// TYPES
import { IManagedPosition, IQueue, IUserWithQueue } from "@/Settings/types/QueueMapping/QueueMapping";

// ICONS
import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";

const getManagedPositions = (managedPositions: string[]): IManagedPosition[] => {
	return managedPositions?.map((pos) => ({ _id: pos.replaceAll(" ", ""), label: pos })) ?? [];
};

interface IQueueMappingSelectorsProps {
	managedPositions: string[];
	selectedPositions: IManagedPosition[];
	queue: IQueue;
	associates: IUserWithQueue[];
	employees: IUserWithQueue[];
	isPositionsLoading: boolean;
	isEmployeesLoading: boolean;
	isUsersLoading: boolean;
	onPositionsChange: (selected: IManagedPosition[]) => void;
	onAssociatesChange: (selected: IUserWithQueue[]) => void;
	onEmployeesChange: (selected: IUserWithQueue[]) => void;
	uniqueUsers: string[] | ReactNode;
	nameError: { error: boolean; name: string };
}

const QueueMappingSelectors = (props: IQueueMappingSelectorsProps) => {
	const {
		managedPositions,
		selectedPositions,
		onPositionsChange,
		queue,
		associates,
		employees,
		isPositionsLoading,
		isEmployeesLoading,
		isUsersLoading,
		onAssociatesChange,
		onEmployeesChange,
		uniqueUsers,
		nameError,
	} = props;

	return (
		<>
			<Stack display={"flex"} direction={"row"} justifyContent={"space-between"} gap={2}>
				<Stack display={"flex"} direction={"column"} gap={2}>
					<PositionsMultiSelect
						positions={getManagedPositions(managedPositions)}
						selected={selectedPositions}
						loading={isPositionsLoading}
						onPositionsChange={(selected) => onPositionsChange(selected)}
						error={selectedPositions.length === 0 && queue?.usersByPosition?.length === 0}
						disabled={nameError.error}
					/>
				</Stack>
				<Stack display={"flex"} direction={"column"} gap={2}>
					<UsersMultiSelect
						queue={queue}
						onUserChange={onAssociatesChange}
						loading={isUsersLoading}
						users={associates ?? []}
						disabled={nameError.error || !selectedPositions.length}
						uniqueUsers={uniqueUsers}
						usersType={"Associates"}
						error={!queue?.usersByPosition?.length}
						footer={<UsersMultiSelectFooter />}
					/>
				</Stack>
				<Stack display={"flex"} direction={"column"} gap={2} mr={8}>
					<UsersMultiSelect
						queue={queue}
						onUserChange={onEmployeesChange}
						loading={isEmployeesLoading}
						users={employees ?? []}
						disabled={nameError.error}
						uniqueUsers={[]}
						error={!queue?.usersByPosition?.length}
						usersType={"Other Employees"}
					/>
				</Stack>
			</Stack>
			<Stack display={"flex"} direction={"column"} gap={1}>
				{nameError.error && (
					<Stack display={"flex"} direction={"row"} alignItems={"center"} mb={0} gap={1}>
						<InformationIcon className="fill-red-500" />
						<Typography className="!font-sans !text-sm/[18px] !text-error">
							Queue name must be unique and cannot be named “{nameError.name}.”
						</Typography>
					</Stack>
				)}
				{queue.usersByPosition.length === 0 && (
					<Stack display={"flex"} direction={"row"} alignItems={"center"} gap={1}>
						<InformationIcon className="fill-red-500" />
						<Typography className="!font-sans !text-sm/[18px] !text-error">
							Queue must have at least one individual selected.
						</Typography>
					</Stack>
				)}
			</Stack>
		</>
	);
};

export default QueueMappingSelectors;
